import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { syncGateReason } from "../lib/gate_reason"

// The reason the checkout primary gives while the request is in flight
// (A11Y-1 / BUTTON-4): disabled for a different reason than the minimum, and
// it says so rather than going silent.
export const BUSY_REASON = "Sending you to checkout…"
// The same, when an applied voucher pays and no checkout page follows (#14864).
export const VOUCHER_BUSY_REASON = "Completing your purchase…"
export const CHECKOUT_LABEL = "Proceed to Checkout"
export const VOUCHER_LABEL = "Complete Purchase"
// Fallback minimum when a host renders the amount input without a `min`
// (the view derives `min` from LeadVoucher.minimum_amount_dollars, so this is
// never the source of truth on a real page).
const FALLBACK_MINIMUM_DOLLARS = 20

export default class extends Controller {
  static targets = [
    "amountInput", "amountButtons", "displayAmount", "displayTotal", "submitButton",
    // Pay with another voucher (#14864)
    "voucherCodeInput", "applyButton", "appliedCode", "voucherStatus", "voucherStatusText",
    "voucherSummary", "voucherSummaryTemplate", "displayVoucherDeduction",
  ]

  connect() {
    this.inFlight = false
    this.appliedVoucher = null
    this.updateDisplayFromInput()
    this.wireCollapseCarets()
  }

  disconnect() {
    this.caretListeners?.abort()
  }

  // Flip the caret on the How It Works / Voucher Terms panel headers as they
  // expand/collapse. Re-run after an error swap so the fresh DOM is wired too.
  // Listeners are tracked on an AbortController so re-wiring (or a Turbo cache
  // restore re-firing connect) never accumulates duplicates.
  wireCollapseCarets() {
    this.caretListeners?.abort()
    this.caretListeners = new AbortController()
    const { signal } = this.caretListeners
    this.element.querySelectorAll('[data-bs-toggle="collapse"]').forEach((toggle) => {
      const selector = toggle.getAttribute("data-bs-target")
      const target = selector && this.element.querySelector(selector)
      const icon = toggle.querySelector(".ph-caret-down, .ph-caret-up")
      if (!target || !icon) return
      target.addEventListener("show.bs.collapse", () => icon.classList.replace("ph-caret-down", "ph-caret-up"), { signal })
      target.addEventListener("hide.bs.collapse", () => icon.classList.replace("ph-caret-up", "ph-caret-down"), { signal })
    })
  }

  // The voucher form lives inside a modal but checkout redirects to Stripe's
  // hosted page (an external URL Turbo can't follow). So we submit via
  // request.js: on success we navigate the browser to Stripe; on a validation
  // or Stripe error the server returns the re-rendered form partial, which we
  // swap back into this controller's element so the error stays in the modal.
  //
  // ONE request at a time: `inFlight` is the explicit guard. While it is set a
  // second submit (double click, Enter, a programmatic requestSubmit) posts
  // nothing, and every amount edit keeps the primary disabled with the busy
  // reason instead of re-enabling it under the pending checkout. When the
  // request settles without leaving the page, validity is recomputed from the
  // amount the field holds NOW — so an amount edited below the minimum during
  // the request stays disabled (with the minimum reason), and a valid one is
  // enabled again for a retry.
  async submit(event) {
    event.preventDefault()
    if (this.inFlight || !this.amountReady() || this.voucherShort() ||
      (this.hasSubmitButtonTarget && this.purchaseBlockedReason())) {
      this.syncSubmit()
      return
    }

    const form = event.currentTarget
    this.inFlight = true
    this.syncSubmit()

    let response
    try {
      response = await post(form.action, {
        body: new FormData(form),
        responseKind: "json",
      })
    } catch {
      // Network failure (offline, timeout, connection refused). Tell the user
      // and let them retry — the modal DOM is reused by Bootstrap, so
      // disconnect() never fires to reset it otherwise.
      window.showAlertModal?.("Connection error — please check your network and try again.")
      this.settle()
      return
    }

    let data = null
    try {
      data = await response.json
    } catch {
      data = null
    }

    if (response.ok && data && data.status === "redirect") {
      // Leaving the page: the primary stays disabled with the busy reason so a
      // click in the hand-off gap cannot open a second checkout.
      this.navigateTo(data.url)
      return
    }

    if (data && data.status === "error" && data.html) {
      // Replacing innerHTML keeps this controller connected; Stimulus re-binds
      // the targets/actions in the freshly rendered form. The re-rendered
      // partial opens with an empty amount, so the primary is re-synced from
      // the fresh field the same way connect() does.
      this.element.innerHTML = data.html
      // The fresh form carries no applied voucher; forget the old one.
      this.appliedVoucher = null
      this.wireCollapseCarets()
      this.settle()
      return
    }

    if (data && data.status === "error" && data.message) {
      // Message-only errors (e.g. the purchase rate limit's 429) have no form
      // re-render — surface the message and let the user retry later.
      window.showAlertModal?.(data.message)
      // The balance moved since Apply: the applied figure is stale, so drop it
      // and let the buyer re-apply to see what is really left.
      if (data.error === "insufficient_balance") this.setAppliedVoucher(null)
      this.settle()
      return
    }

    // Unexpected response — recompute the primary so the user can retry.
    this.settle()
  }

  // --- Pay with another voucher (#14864) ----------------------------------
  //
  // Apply asks the server (LeadVouchers::FundingVoucher, the SAME rule the
  // purchase re-runs under lock) whether the code can pay. An applied code is
  // copied into the hidden voucher_code field — the only field the server
  // reads as the payment code — so typing a code without applying it never
  // switches the payment lane. A code that is valid but short of the amount
  // stays applied (the buyer may lower the amount) and the primary says why
  // it is disabled; the styled alert tells them at Apply time.
  async applyVoucher(event) {
    event.preventDefault()
    const code = this.voucherCodeInputTarget.value.trim()
    if (!code) {
      window.showAlertModal?.("Enter a voucher code to apply.")
      return
    }

    const body = new FormData()
    body.append("code", code)
    body.append("amount_dollars", this.amountInputTarget.value)
    body.append("recipient_name", this.recipientName())
    body.append("recipient_email", this.recipientEmail())

    let data = null
    try {
      const response = await post(this.applyButtonTarget.dataset.applyUrl, { body, responseKind: "json" })
      data = await response.json
    } catch {
      window.showAlertModal?.("Connection error — please check your network and try again.")
      return
    }

    if (data && (data.valid || data.error === "insufficient_balance") && data.code) {
      this.setAppliedVoucher({ code: data.code, availableCents: data.available_cents, available: data.available })
      if (!data.valid) window.showAlertModal?.(data.message)
      return
    }

    this.setAppliedVoucher(null)
    window.showAlertModal?.(data?.message || "This voucher cannot be used.")
  }

  removeVoucher(event) {
    event?.preventDefault()
    this.setAppliedVoucher(null)
    this.voucherCodeInputTarget.value = ""
  }

  setAppliedVoucher(voucher) {
    this.appliedVoucher = voucher
    this.appliedCodeTarget.value = voucher ? voucher.code : ""
    this.voucherStatusTarget.hidden = !voucher
    this.toggleVoucherSummary(!!voucher)
    this.voucherStatusTextTarget.textContent = voucher
      ? `Voucher ${voucher.code} applied — ${voucher.available} available.`
      : ""
    this.submitButtonTarget.textContent = voucher ? VOUCHER_LABEL : CHECKOUT_LABEL
    this.updateDisplayFromInput()
  }

  // The "Paid With Voucher" column is rendered only while a voucher is
  // applied: stamped from its <template> in place (between Amount and Total)
  // and removed again, so the default summary never holds a hidden static.
  toggleVoucherSummary(show) {
    if (show && !this.hasVoucherSummaryTarget && this.hasVoucherSummaryTemplateTarget) {
      const template = this.voucherSummaryTemplateTarget
      template.before(template.content.cloneNode(true))
    } else if (!show && this.hasVoucherSummaryTarget) {
      this.voucherSummaryTarget.remove()
    }
  }

  // True when an applied voucher cannot cover the whole amount — no partial
  // funding (owner ruling 2026-09-29).
  voucherShort() {
    return !!this.appliedVoucher && this.currentAmountCents() > this.appliedVoucher.availableCents
  }

  insufficientReason() {
    const recipient = this.recipientName() || "the agent"
    return `Your voucher balance of ${this.appliedVoucher.available} is not enough to pay for the ` +
      `${this.formatCurrency(this.currentAmountCents())} lead voucher for ${recipient}.`
  }

  recipientName() {
    return this.fieldValue("lead_voucher[recipient_name]")
  }

  recipientEmail() {
    return this.fieldValue("lead_voucher[recipient_email]")
  }

  fieldValue(name) {
    const field = this.element.querySelector(`[name="${name}"]`)
    return field ? field.value.trim() : ""
  }

  // Wrapped so tests can stub navigation — jsdom's Location is non-configurable.
  navigateTo(url) {
    window.location.assign(url)
  }

  // The request is over and the page is still here: the primary's state is
  // whatever the CURRENT amount says, never a blind re-enable.
  settle() {
    this.inFlight = false
    this.updateDisplayFromInput()
  }

  selectAmount(event) {
    event.preventDefault()
    const button = event.currentTarget
    const amountCents = parseInt(button.dataset.amount)
    const amountDollars = amountCents / 100

    // Update the input field with dollar amount
    this.amountInputTarget.value = amountDollars

    // Update button states
    this.updateButtonStates(button)

    // Update display
    this.updateDisplay(amountCents)
  }

  updateAmount(event) {
    const dollarValue = parseFloat(event.target.value) || 0
    const amountCents = Math.round(dollarValue * 100)

    // Clear button selection when typing custom amount
    this.clearButtonStates()

    // Update display
    this.updateDisplay(amountCents)
  }

  updateDisplayFromInput() {
    if (!this.hasAmountInputTarget) return
    this.updateDisplay(this.currentAmountCents())
  }

  updateDisplay(amountCents) {
    const formattedAmount = this.formatCurrency(amountCents)
    this.displayAmountTarget.textContent = formattedAmount
    // An applied voucher pays the WHOLE amount or nothing (no partial funding),
    // so a short balance deducts $0.00 and the total stays the full amount —
    // the summary never shows a payment the purchase would refuse.
    if (this.appliedVoucher && this.hasDisplayVoucherDeductionTarget) {
      const covered = amountCents <= this.appliedVoucher.availableCents
      this.displayVoucherDeductionTarget.textContent = `−${covered ? formattedAmount : this.formatCurrency(0)}`
      this.displayTotalTarget.textContent = covered ? this.formatCurrency(0) : formattedAmount
    } else {
      this.displayTotalTarget.textContent = formattedAmount
    }
    this.syncSubmit()
  }

  // The primary's state and reason, from one place: busy while a request is in
  // flight, the minimum while the amount is below it, enabled otherwise — the
  // reason applied as the button's gl-tooltip plus its "<label> — <reason>"
  // accessible name, and both withdrawn when it is enabled (lib/gate_reason).
  syncSubmit() {
    if (!this.hasSubmitButtonTarget) return
    // GOAT Store Buy switch (#14937): a server-painted reason outranks every
    // other state — no amount, voucher or retry can enable a closed purchase.
    const blocked = this.purchaseBlockedReason()
    if (blocked) {
      syncGateReason(this.submitButtonTarget, { ready: false, reason: blocked })
      return
    }
    if (this.inFlight) {
      const reason = this.appliedVoucher ? VOUCHER_BUSY_REASON : BUSY_REASON
      syncGateReason(this.submitButtonTarget, { ready: false, reason })
      return
    }
    if (this.amountReady() && this.voucherShort()) {
      syncGateReason(this.submitButtonTarget, { ready: false, reason: this.insufficientReason() })
      return
    }
    syncGateReason(this.submitButtonTarget, { ready: this.amountReady(), reason: this.disabledReason() })
  }

  amountReady() {
    return this.hasAmountInputTarget && this.currentAmountCents() >= this.minimumCents()
  }

  currentAmountCents() {
    const dollarValue = parseFloat(this.amountInputTarget.value) || 0
    return Math.round(dollarValue * 100)
  }

  // The minimum is the amount input's own `min` (dollars), which the view
  // derives from LeadVoucher.minimum_amount_dollars — one source, no second
  // constant to drift from it.
  minimumCents() {
    const min = this.hasAmountInputTarget ? parseFloat(this.amountInputTarget.min) : NaN
    return Math.round((Number.isFinite(min) ? min : FALLBACK_MINIMUM_DOLLARS) * 100)
  }

  purchaseBlockedReason() {
    return (this.submitButtonTarget.dataset.leadVoucherFormPurchaseBlockedReason || "").trim()
  }

  // The reason the view painted (data-lead-voucher-form-disabled-reason), else
  // one derived from the same minimum the input carries.
  disabledReason() {
    return this.submitButtonTarget.dataset.leadVoucherFormDisabledReason ||
      `Enter a voucher amount of at least $${this.minimumCents() / 100}`
  }

  updateButtonStates(selectedButton) {
    // Clear the selected state on every amount button, then mark the chosen one.
    // `.gl-amount-btn.selected` carries the primary fill in both themes
    // (_form-components.scss / _dark-mode.scss).
    this.amountButtonsTarget.querySelectorAll('.gl-amount-btn').forEach(btn => {
      btn.classList.remove('selected')
    })

    selectedButton.classList.add('selected')
  }

  clearButtonStates() {
    this.amountButtonsTarget.querySelectorAll('.gl-amount-btn').forEach(btn => {
      btn.classList.remove('selected')
    })
  }

  formatCurrency(cents) {
    return '$' + (cents / 100).toFixed(2)
  }
}
